import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import Button from "veneer/Button";
import Stack from "veneer/Stack";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { CHANGE_PASSWORD } from "cortex/ui/src/redux-store/users/actions";
import BackButton from "cortex/ui/src/ui/button";
import { PasswordInput } from "cortex/ui/src/ui/form";
import Notification from "cortex/ui/src/ui/notification";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";
import { Container } from "cortex/ui/src/view/layout/Container";

const defaultFormValues: { p1: string; p2: string } = {
  p1: "",
  p2: "",
};

export default function Settings() {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<{ p1: string; p2: string }>(
    defaultFormValues,
  );
  const [passwordMeta, setPasswordMeta] = useState({ valid: false, score: 0 });
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await createPromiseApiAction(
        dispatch,
        CHANGE_PASSWORD,
        [form.p1, form.p2],
        "Your password updated successfully.",
      );
      setSuccessMessage("Your password updated successfully.");
      setErrorMessage("");
    } catch (e: unknown) {
      setErrorMessage(getErrorMessage(e as Error | ResponseLike | null));
      setSuccessMessage("");
    } finally {
      setSubmitted(false);
    }
  }, [dispatch, form.p1, form.p2]);

  return (
    <Container p={1} title="Change Password">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Stack gap={2} width={500}>
          {(errorMessage || successMessage) && (
            <Notification
              error={errorMessage}
              success={successMessage}
              successChildren={<Link to="/overview">Account Overview</Link>}
            />
          )}
          <PasswordInput
            minLength={8}
            onChange={(next, meta) => {
              setForm((prev) => ({
                ...prev,
                p1: next.password,
                p2: next.retype ?? "",
              }));
              setPasswordMeta(meta);
            }}
            size="small"
            value={{ password: form.p1 ?? "", retype: form.p2 ?? "" }}
            withRetype
          />
          <Stack alignItems="center" direction="row" gap={1}>
            <BackButton />
            <Button
              disabled={submitted || !passwordMeta.valid}
              loading={submitted}
              onClick={handleSubmit}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </form>
    </Container>
  );
}
